import { _ } from "i18n";
import type { Keys } from "i18n/locales/types";
import { ProviderOperationsAction, type ProviderOperationsSearchRowResponse } from "libs/apiClient";
import { useEffect, useState } from "react";
import { getProviderOperationsBulkPlan } from "./getProviderOperationsBulkPlan";
import { getProviderOperationsBulkSummary, type ProviderOperationsBulkSummary } from "./getProviderOperationsBulkSummary";
import { getProviderOperationsRowKey } from "./getProviderOperationsRowKey";
import { ProviderOperationsBulkConfirmModal } from "./ProviderOperationsBulkConfirmModal";
import { PROVIDER_OPERATIONS_ACTION_LABEL_KEYS } from "./providerOperationsActionLabelKeys";
import {
	type ProviderOperationsBulkAction,
	useIsAnyRowActionRunning,
	useIsBulkActionRunning,
} from "./useProviderOperationsBulkAction";

type ProviderOperationsBulkToolbarProps = {
	rows: ProviderOperationsSearchRowResponse[];
	bulkAction: ProviderOperationsBulkAction;
};

export function ProviderOperationsBulkToolbar({ rows, bulkAction }: ProviderOperationsBulkToolbarProps) {
	const [actionBeingConfirmed, setActionBeingConfirmed] = useState<ProviderOperationsAction>();
	const [summary, setSummary] = useState<ProviderOperationsBulkSummary>();
	const [alreadyRunRowKeysByAction, setAlreadyRunRowKeysByAction] = useState<
		Partial<Record<ProviderOperationsAction, ReadonlySet<string>>>
	>({});
	const { progress, runBulkAction, stopBulkAction } = bulkAction;
	const isBulkActionRunning = useIsBulkActionRunning();
	const isAnyRowActionRunning = useIsAnyRowActionRunning();
	const isActionRunning = isBulkActionRunning || isAnyRowActionRunning;

	useEffect(() => {
		return () => stopBulkAction();
	}, [stopBulkAction]);

	const getPlan = (action: ProviderOperationsAction) =>
		getProviderOperationsBulkPlan(rows, action, alreadyRunRowKeysByAction[action]);

	const runConfirmedAction = async (action: ProviderOperationsAction) => {
		const plan = getPlan(action);
		setActionBeingConfirmed(undefined);
		setSummary(undefined);

		const rowOutcomes = await runBulkAction(action, plan.rowsToRun);

		setSummary(getProviderOperationsBulkSummary(plan, rowOutcomes));
		setAlreadyRunRowKeysByAction((previousRowKeysByAction) => ({
			...previousRowKeysByAction,
			[action]: new Set([
				...(previousRowKeysByAction[action] ?? []),
				...plan.rowsToRun.map(getProviderOperationsRowKey),
			]),
		}));
	};

	return (
		<div className="provider-operations-bulk">
			<div className="provider-operations-actions">
				<span className="provider-operations-bulk__label">{_("providerOperations.bulk.title" as keyof Keys)}</span>
				{ProviderOperationsAction.options.map((action) => {
					const rowsToRunCount = getPlan(action).rowsToRun.length;

					return (
						<button
							key={action}
							className="admin-button admin-button--primary admin-button--xs"
							type="button"
							disabled={rowsToRunCount === 0 || isActionRunning}
							onClick={() => setActionBeingConfirmed(action)}
						>
							{action === progress?.action && progress.isStopping === false
								? `${_("providerOperations.action.running" as keyof Keys)} ${progress.completedCount}/${progress.totalCount}`
								: `${_(PROVIDER_OPERATIONS_ACTION_LABEL_KEYS[action] as keyof Keys)} (${rowsToRunCount})`}
						</button>
					);
				})}
				{progress?.isStopping === true && (
					<span className="provider-operations-muted">
						{_("providerOperations.bulk.finishingPreviousRun" as keyof Keys)}
					</span>
				)}
			</div>
			{summary !== undefined && (
				<ul className="provider-operations-bulk-summary">
					<li>
						{_("providerOperations.bulk.succeeded" as keyof Keys)}: {summary.succeededCount}
					</li>
					<li>
						{_("providerOperations.bulk.failed" as keyof Keys)}: {summary.failedCount}
					</li>
					<li>
						{_("providerOperations.bulk.skipped" as keyof Keys)}: {summary.skippedCount}
					</li>
				</ul>
			)}
			{actionBeingConfirmed !== undefined && (
				<ProviderOperationsBulkConfirmModal
					action={actionBeingConfirmed}
					plan={getPlan(actionBeingConfirmed)}
					isActionRunning={isActionRunning}
					onConfirm={() => runConfirmedAction(actionBeingConfirmed)}
					onCancel={() => setActionBeingConfirmed(undefined)}
				/>
			)}
		</div>
	);
}
